import React from 'react';
import { Star, RefreshCw, Users, MessageCircle } from 'lucide-react';

const ReviewStats = ({ stats }) => {
  // If no community reviews, show welcoming empty state
  const hasReviews = stats && stats.totalReviews > 0;
  
  const { totalReviews = 0, averageRating = 0, wouldRepurchasePercent = 0, ratingDistribution = {}, skinTypeBreakdown = {} } = stats || {};

  // Render rating stars
  const renderStars = (rating, size = 'h-5 w-5') => {
    const stars = [];
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 >= 0.25;

    for (let i = 1; i <= 5; i++) {
      if (i <= fullStars) {
        stars.push(
          <Star key={i} className={`${size} text-[#ab3d22] fill-current`} />
        );
      } else if (i === fullStars + 1 && hasHalfStar) {
        stars.push(
          <div key={i} className="relative inline-block">
            <Star className={`${size} text-gray-200`} />
            <Star 
              className={`${size} text-[#ab3d22] fill-current absolute top-0 left-0`}
              style={{ clipPath: 'inset(0 50% 0 0)' }}
            />
          </div>
        );
      } else {
        stars.push(
          <Star key={i} className={`${size} text-gray-200`} />
        );
      }
    }
    return stars;
  };

  // Format skin type label
  const formatSkinType = (type) => {
    const labels = {
      dry: 'Dry',
      oily: 'Oily', 
      combination: 'Combo',
      normal: 'Normal',
      sensitive: 'Sensitive'
    };
    return labels[type] || type;
  };

  // Calculate skin type percentages
  const skinTypeTotal = Object.values(skinTypeBreakdown).reduce((a, b) => a + b, 0);
  const skinTypePercentages = Object.entries(skinTypeBreakdown)
    .map(([type, count]) => ({
      type,
      label: formatSkinType(type),
      percent: skinTypeTotal > 0 ? Math.round((count / skinTypeTotal) * 100) : 0
    }))
    .sort((a, b) => b.percent - a.percent)
    .slice(0, 3);

  // Empty state - inviting users to be first (NO sample review)
  if (!hasReviews) {
    return (
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-white via-[#fdf9f7] to-[#f8f3f0] border border-[#e8ddd6] shadow-[0_8px_32px_rgba(171,61,34,0.08)]">
        {/* Decorative background elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-12 -right-12 w-40 h-40 bg-gradient-to-br from-[#ab3d22]/10 to-transparent rounded-full blur-2xl"></div>
          <div className="absolute -bottom-8 -left-8 w-32 h-32 bg-gradient-to-tr from-[#ab3d22]/8 to-transparent rounded-full blur-2xl"></div>
          {/* Subtle pattern overlay */}
          <div className="absolute inset-0 opacity-[0.03]" style={{
            backgroundImage: `radial-gradient(circle at 2px 2px, #ab3d22 1px, transparent 0)`,
            backgroundSize: '24px 24px'
          }}></div>
        </div>
        
        <div className="relative p-8 text-center">
          {/* Animated icon container */}
          <div className="relative w-20 h-20 mx-auto mb-5">
            <div className="absolute inset-0 bg-gradient-to-br from-[#ab3d22]/20 to-[#ab3d22]/5 rounded-2xl rotate-6 transition-transform hover:rotate-12"></div>
            <div className="absolute inset-0 bg-gradient-to-br from-[#ab3d22] to-[#8a2f1a] rounded-2xl shadow-lg shadow-[#ab3d22]/25 flex items-center justify-center">
              <MessageCircle className="h-9 w-9 text-white" />
            </div>
          </div>
          
          <h4 className="text-xl font-bold text-gray-900 mb-2" style={{fontFamily: 'Poppins, sans-serif'}}>
            Be the First to Review
          </h4>
          <p className="text-gray-600 text-sm mb-4 max-w-sm mx-auto leading-relaxed">
            Your honest experience helps others make smarter choices. Share your thoughts below!
          </p>
          
          {/* Stats preview - what they'll see */}
          <div className="flex items-center justify-center gap-4 text-xs text-gray-400">
            <span className="flex items-center gap-1">
              <Star className="h-3.5 w-3.5" />
              Rate it
            </span>
            <span className="w-1 h-1 bg-gray-300 rounded-full"></span>
            <span className="flex items-center gap-1">
              <RefreshCw className="h-3.5 w-3.5" />
              Would repurchase?
            </span>
            <span className="w-1 h-1 bg-gray-300 rounded-full"></span>
            <span className="flex items-center gap-1">
              <Users className="h-3.5 w-3.5" />
              Your skin type
            </span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="relative overflow-hidden rounded-2xl border border-[#e5ddd7] shadow-lg shadow-[#ab3d22]/8">
      {/* Background with depth */}
      <div className="absolute inset-0 bg-gradient-to-br from-white via-[#fdf9f7] to-[#f8f3ef]"></div>
      <div className="absolute -top-16 -right-16 w-40 h-40 bg-[#ab3d22]/[0.04] rounded-full blur-3xl"></div>
      
      {/* Community Rating Header */}
      <div className="relative px-5 py-3.5 border-b border-[#e8ddd6] bg-gradient-to-r from-white/90 to-white/70 backdrop-blur-sm">
        <div className="flex items-center gap-2.5">
          <div className="w-7 h-7 bg-gradient-to-br from-[#ab3d22] to-[#8a2f1a] rounded-lg flex items-center justify-center shadow-sm">
            <Users className="h-3.5 w-3.5 text-white" />
          </div>
          <span className="text-sm font-semibold text-gray-800">Community Rating</span>
        </div>
      </div>

      {/* Main stats row */}
      <div className="relative p-5">
        <div className="flex items-start gap-5">
          {/* Community Rating - Big number */}
          <div className="flex-shrink-0 text-center min-w-[90px]">
            <div className="flex items-baseline justify-center">
              <span className="text-5xl font-bold bg-gradient-to-br from-gray-900 to-gray-700 bg-clip-text text-transparent">{averageRating}</span>
              <span className="text-lg text-gray-400 ml-1">/5</span>
            </div>
            <div className="flex items-center justify-center gap-0.5 mt-2">
              {renderStars(averageRating, 'h-4 w-4')}
            </div>
            <p className="text-sm text-gray-500 mt-1.5 font-medium">
              {totalReviews} {totalReviews === 1 ? 'review' : 'reviews'}
            </p>
          </div>

          {/* Rating Distribution Bars */}
          <div className="flex-1 space-y-1.5">
            {[5, 4, 3, 2, 1].map((star) => {
              const count = ratingDistribution[star] || 0;
              const percent = totalReviews > 0 ? (count / totalReviews) * 100 : 0;
              
              return (
                <div key={star} className="flex items-center gap-2">
                  <span className="text-xs text-gray-500 w-3 text-right font-medium">{star}</span>
                  <Star className="h-3.5 w-3.5 text-[#ab3d22] fill-current flex-shrink-0" />
                  <div className="flex-1 h-2.5 bg-white rounded-full overflow-hidden border border-[#e8ddd6] shadow-inner">
                    <div 
                      className="h-full bg-gradient-to-r from-[#ab3d22] to-[#c45a3d] rounded-full transition-all duration-500"
                      style={{ width: `${percent}%` }}
                    />
                  </div>
                  <span className="text-xs text-gray-500 w-6 text-right font-medium">{count}</span>
                </div>
              );
            })}
          </div>

          {/* Repurchase Badge */}
          <div className="flex-shrink-0">
            <div className="relative overflow-hidden bg-gradient-to-br from-green-50 to-emerald-50 border border-green-200/80 rounded-xl px-4 py-3 text-center min-w-[95px] shadow-sm">
              <div className="absolute -top-4 -right-4 w-12 h-12 bg-green-400/10 rounded-full blur-xl"></div>
              <div className="relative">
                <div className="flex items-center justify-center gap-1.5 mb-1">
                  <RefreshCw className="h-4 w-4 text-green-600" />
                </div>
                <div className="text-2xl font-bold text-green-700">{wouldRepurchasePercent}%</div>
                <div className="text-xs text-green-600 leading-tight font-medium mt-0.5">would<br/>repurchase</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Skin type breakdown - only if we have data */}
      {skinTypePercentages.length > 0 && (
        <div className="relative px-5 py-3 bg-gradient-to-r from-white/80 to-white/60 border-t border-[#e8ddd6]">
          <div className="flex items-center gap-3">
            <span className="text-xs text-gray-500 font-medium">Reviewers:</span>
            <div className="flex items-center gap-4">
              {skinTypePercentages.map(({ label, percent }) => (
                <span key={label} className="text-sm">
                  <span className="font-bold text-gray-700">{percent}%</span>
                  <span className="text-gray-500 ml-1">{label}</span>
                </span>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReviewStats;
